import React, { useEffect, useState } from "react"
import { Collapsible as CollapsiblePrimitive } from "@base-ui/react/collapsible"
import { cn } from "../utils"

// =============================================================================
// Styles
// =============================================================================
const panelStyles = [
  // content sticks to the bottom of the panel box so it appears to be sliding out from the trigger
  "flex flex-col justify-end",
  // handle hidden="until-found" correctly
  "[&[hidden]:not([hidden='until-found'])]:hidden",
  // --collapsible-panel-height is measured and exposed by Base UI. Clips only
  // vertically, so a nested section's trigger isn't cut off at the sides, and
  // only while animating, so a menu in the content can reach past the bottom.
  "h-[var(--collapsible-panel-height)] overflow-x-visible overflow-y-clip data-open:data-settled:overflow-y-visible",
  "transition-[height] ease-out motion-reduce:transition-none",
  "group/collapsible-content data-starting-style:h-0 data-ending-style:h-0",
  // Fades the content out as it slides under the clipped top edge while closing.
  "data-ending-style:[mask-image:linear-gradient(to_bottom,transparent,black_1rem)]",
  // Room for focus rings above and below the content. The top 4px overlaps
  // the trigger, so the panel lets clicks through.
  "-my-1 py-1 pointer-events-none"
]

const contentStyles = [
  "pointer-events-auto flex flex-col",
  // Fades with the height, hiding the clipped edge and the sliver the panel's
  // padding leaves at either end of the animation.
  "transition-opacity ease-in group-data-ending-style/collapsible-content:ease-out motion-reduce:transition-none",
  "group-data-starting-style/collapsible-content:opacity-0 group-data-ending-style/collapsible-content:opacity-0"
]

// =============================================================================
// Props
// =============================================================================
export type CollapsibleContentProps = {
  children?: React.ReactNode
  /** Additional CSS classes, such as spacing between the children */
  className?: string
  /**
   * Keeps the closed content in the page as `hidden="until-found"`, so the
   * browser's find in page can match it and opens it when it does. On by
   * default; turn it off to unmount closed content, such as content that
   * loads when opened.
   */
  hiddenUntilFound?: boolean
  /** Keeps the closed content mounted, hidden, instead of unmounting it */
  keepMounted?: boolean
}

// =============================================================================
// Component
// =============================================================================
/** The content shown while open. Animates its height when opening and closing. */
export function CollapsibleContent({
  children,
  className,
  hiddenUntilFound = true,
  keepMounted
}: CollapsibleContentProps) {
  const [panel, setPanel] = useState<HTMLDivElement | null>(null)
  const settled = useSettled(panel)

  return (
    <CollapsiblePrimitive.Panel
      ref={setPanel}
      data-slot="collapsible-content"
      data-settled={settled || undefined}
      hiddenUntilFound={hiddenUntilFound}
      keepMounted={keepMounted}
      className={cn(panelStyles)}
    >
      <div className={cn(contentStyles, className)}>{children}</div>
    </CollapsiblePrimitive.Panel>
  )
}

CollapsibleContent.displayName = "Collapsible.Content"

/**
 * Whether the panel is open and done animating. Base UI has no attribute for
 * that, but resets the panel height to `auto` once the opening animation
 * finishes, or straight away when there's none to run.
 */
function useSettled(panel: HTMLDivElement | null) {
  const [settled, setSettled] = useState(false)

  useEffect(() => {
    if (!panel) return

    const update = () =>
      setSettled(
        panel.hasAttribute("data-open") &&
          panel.style.getPropertyValue("--collapsible-panel-height") === "auto"
      )
    update()

    const observer = new MutationObserver(update)
    observer.observe(panel, {
      attributes: true,
      attributeFilter: ["style", "data-open"]
    })
    return () => observer.disconnect()
  }, [panel])

  return settled
}
